import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import SabiasQue from "../SabiasQue";

// Template36: Página Extra - Este de Kioto (Ginkaku-ji y Camino de la Filosofía) (Kioto)
// Contenido desde page.texts, page.images, page.canvasImages y page.maps (servidor).
const Template36 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onMapFullscreenChange,
}) => {
	const isSmallScreen = window.innerWidth <= 600;
	const [loaded, setLoaded] = useState(navigator.onLine);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(navigator.onLine);

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const parseSabiasQueItem = (html) => {
		if (!html) return null;
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const p = doc.querySelector("p");
		if (!p) return null;
		const text = (p.textContent ?? p.innerText ?? "").trim();
		const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
		if (emojiMatch)
			return { icono: emojiMatch[1], texto: emojiMatch[2].trim() };
		return null;
	};

	const headerText = page?.texts?.[0]?.content;
	const ginkakujiTitle = page?.texts?.[1]?.content;
	const ginkakujiText = page?.texts?.[2]?.content;
	const caminoText = page?.texts?.[3]?.content;
	const transporteTitle = page?.texts?.[4]?.content;
	const transporteText1 = page?.texts?.[5]?.content;
	const transporteText2 = page?.texts?.[6]?.content;
	const transporteText3 = page?.texts?.[7]?.content;
	const sabiasQuePlata = parseSabiasQueItem(page?.texts?.[8]?.content);
	const sabiasQueCamino = parseSabiasQueItem(page?.texts?.[9]?.content);

	const mapUrl = page?.maps?.[0];
	const temploPlateadoImage = getImageSrc("templo-plateado");
	const logoImage = getCanvasImageUrl("logo");
	const caminoSticker = getCanvasImageUrl("camino-filosofia");

	useEffect(() => {
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	useEffect(() => {
		const style = document.createElement("style");
		style.innerHTML = `
			@keyframes spin {
				0% { transform: rotate(0deg); }
				100% { transform: rotate(360deg); }
			}
		`;
		document.head.appendChild(style);
		return () => document.head.removeChild(style);
	}, []);

	if (fullscreen && mapUrl) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: "#5ba374",
					}}
				>
					<Box
						component="button"
						onClick={() => {
							setFullscreen(false);
							onMapFullscreenChange?.(false);
						}}
						sx={{
							backgroundColor: "#fff",
							color: "#5ba374",
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						}}
					>
						← Volver a la guía
					</Box>
				</Box>
				<Box
					component="iframe"
					title="Mapa grande"
					src={mapUrl}
					width="100%"
					height="100%"
					sx={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				/>
			</Box>
		);
	}

	const cardContainer = {
		display: "flex",
		gap: "16px",
		padding: "0 12px",
		marginBottom: "16px",
		position: "relative",
	};

	const cardImage = {
		width: "40%",
		height: 100,
		objectFit: "cover",
		borderRadius: "12px",
	};

	const cardContent = { width: "60%" };

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const contentColor = textColor || "#444";

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "0px !important",
				},
				"& .margin": {
					marginBottom: "6px !important",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: headerBackgroundColor || "#5ba374",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1053,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			<Box data-block-id="map" sx={{ flex: 1, marginTop: "-70px", position: "relative" }}>
				{!loaded && isOnline && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
						}}
					>
						<Box
							sx={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: "4px solid #5ba374",
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
							}}
						/>
					</Box>
				)}

				{isOnline && mapUrl && (
					<Box
						component="button"
						onClick={() => {
							setFullscreen(true);
							onMapFullscreenChange?.(true);
						}}
						sx={{
							position: "absolute",
							top: 80,
							right: 10,
							backgroundColor: "#5ba374",
							color: "#fff!important",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							textDecoration: "none",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",

							border: "none",
							cursor: "pointer",
						}}
					>
						Ver mapa grande
					</Box>
				)}

				{isOnline && mapUrl && (
					<Box
						component="iframe"
						title="Mapa Este Kioto"
						src={mapUrl}
						width="100%"
						height="374px"
						allowFullScreen=""
						loading="lazy"
						sx={{ border: 0 }}
						onLoad={() => setLoaded(true)}
					/>
				)}

				{caminoSticker && (
					<Box
						component="img"
						src={caminoSticker}
						alt="CaminoSticker"
						sx={{
							width: 110,
							position: "absolute",
							bottom: 0,
							left: "-20px",
						}}
					/>
				)}
			</Box>

			{(ginkakujiTitle || ginkakujiText) && (
				<Box data-block-id="text-1" sx={{ ...cardContainer, marginTop: isSmallScreen ? "10px" : 0 }}>
					{temploPlateadoImage && (
						<Box
							component="img"
							src={temploPlateadoImage}
							alt="Templo"
							sx={cardImage}
						/>
					)}
					<Box sx={cardContent}>
						{ginkakujiTitle && (
							<Box component="h3" sx={h3}>
								{ginkakujiTitle}
							</Box>
						)}
						{ginkakujiText && (
							<Box
								component="p"
								sx={{
									...p,
									margin: "0 !important",
									marginBottom: "0px !important",
								}}
							>
								{ginkakujiText}
							</Box>
						)}
					</Box>
					{sabiasQuePlata && (
						<Box
							sx={{
								position: "absolute",
								top: "10px",
								left: "33%",
							}}
						>
							<SabiasQue
								icono={sabiasQuePlata.icono}
								texto={sabiasQuePlata.texto}
							/>
						</Box>
					)}
					{sabiasQueCamino && (
						<Box
							sx={{
								position: "absolute",
								top: "60px",
								left: "33%",
							}}
						>
							<SabiasQue
								icono={sabiasQueCamino.icono}
								texto={sabiasQueCamino.texto}
							/>
						</Box>
					)}
				</Box>
			)}

			{caminoText && (
				<Box data-block-id="text-2" sx={{ padding: "12px", paddingTop: 0 }}>
					<Box
						sx={{
							display: "flex",
							justifyContent: isSmallScreen ? "flex-end" : "flex-start",
						}}
					>
						<Box
							sx={{
								backgroundColor: "#d9e6de",
								padding: "12px",
								borderRadius: "12px",
								display: "flex",
								gap: "16px",
								alignItems: "center",
								position: "relative",
								width: "100%",
							}}
						>
							<Box sx={{ width: "100%" }}>
								<Box
									component="p"
									sx={{
										...p,
										margin: "0 !important",
										marginBottom: "0px !important",
									}}
								>
									{caminoText}
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>
			)}

			{transporteTitle && (
				<Box
					sx={{
						paddingLeft: "12px",
						paddingRight: "12px",
						paddingBottom: "12px",
					}}
				>
					<Box
						sx={{
							backgroundColor: "#d9e6de",
							border: "3px solid #5ba374",
							borderRadius: "12px",
							padding: "16px 16px",
							fontSize: "12px",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							color: "#333",
							width: "100%",
							position: "relative",
						}}
					>
						<Box
							component="h4"
							sx={{
								margin: 0,
								marginBottom: "8px",
								fontSize: "13px",
								fontWeight: 700,
								display: "flex",
								alignItems: "center",
								gap: "6px",
								color: "#5ba374",
								textTransform: "uppercase",
							}}
						>
							{transporteTitle}
						</Box>
						{transporteText1 && (
							<Box
								component="p"
								className="margin"
								sx={{
									marginTop: "6px",
									fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
									margin: "0 !important",
									marginBottom: "8px !important",
								}}
								dangerouslySetInnerHTML={{ __html: transporteText1 }}
							/>
						)}
						{transporteText2 && (
							<Box
								component="p"
								className="margin"
								sx={{
									marginTop: "8px",
									fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
									margin: "0 !important",
									marginBottom: "8px !important",
								}}
								dangerouslySetInnerHTML={{ __html: transporteText2 }}
							/>
						)}
						{transporteText3 && (
							<Box
								component="p"
								sx={{
									marginTop: "8px",
									fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
									margin: "0 !important",
									marginBottom: "0px !important",
								}}
								dangerouslySetInnerHTML={{ __html: transporteText3 }}
							/>
						)}
					</Box>
				</Box>
			)}
		</Box>
	);
};

Template36.thumbnail = "/images/template-1.png";

export default Template36;
